/**
 * RecordFormPage — F12.D T12.D.1 + F12.H T12.H.12 (konsolidacja 2026-05-12).
 *
 * UNIFIED strona rekordu modułu — obsługuje 3 tryby:
 *  - CREATE (`/modules/:slug/records/new`): formularz tworzenia, bez history/attachments
 *  - VIEW (`/modules/:slug/:recordId`, default): pola read-only z ramkami
 *  - EDIT (klik „Edytuj" w VIEW): inline form, te same miejsca/wymiary pól
 *
 * Konsolidacja: wcześniej 2 pliki (RecordFormPage = tylko create, RecordDetailPage =
 * view+edit) z ~70% duplikacją. Teraz jeden komponent z `isCreate` branchingiem
 * na poziomie hooków/mutations/renderera.
 *
 * TODO T12.D.8: wyodrębnić ViewForm/EditForm/ViewField/EditFieldRow do shared module.
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Archive, ArrowLeft, Clock, Paperclip, Pencil, Save, Trash2, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { FieldRegistry, type ModuleField } from '@/components/fields/FieldRegistry';
import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import { FormField } from '@/components/FormField';
import { parseProcessViewElementConfig } from '@/components/modules/layout-elements/ProcessViewElement';
import { resolveActiveLayout } from '@/components/modules/layout-resolver';
import { usePageConfig } from '@/components/modules/PageConfigPanel';
import { RecordAttachments } from '@/components/modules/RecordAttachments';
import { RecordHistoryTimeline } from '@/components/modules/RecordHistoryTimeline';
import { SourceRecordBadge } from '@/components/modules/SourceRecordBadge';
import { styleToCSS, styleToCSSSplit } from '@/components/modules/style/styleToCSS';
import type { LayoutElementStyle } from '@/components/modules/style/styleTypes';
import { clearIfUnavailable, useDependentOptions } from '@/components/modules/useDependentOptions';
import { ProcessActionButton } from '@/components/processes/ProcessActionButton';
import { ProcessApprovalBanner } from '@/components/processes/ProcessApprovalBanner';
import { ProcessAutostartIndicator } from '@/components/processes/ProcessAutostartIndicator';
import { ProcessHistoryTab } from '@/components/processes/ProcessHistoryTab';
import { Spinner } from '@/components/Spinner';
import { useTabLabel } from '@/components/tabs/useTabLabel';
import { CompanyPicker, type CompanyPickerOption } from '@/components/ui/CompanyPicker';
import { useToast } from '@/contexts/ToastContext';
import { invalidateRecordInstances } from '@/hooks/useActiveInstanceForRecord';
import { useActiveMenuItem } from '@/hooks/useActiveMenuItem';
import { useRecordProcessAutostart } from '@/hooks/useRecordProcessAutostart';
import { type DecisionGate, useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import { extractFieldErrors } from '@/lib/errors';
import { resolveRuleErrorMessage } from '@/lib/field-rule-validation';
import { parseMenuViewRef } from '@/lib/module-templates/template-layouts';
import { type NumberingConfigV2, previewRecordNumber } from '@/lib/numbering-preview';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useMyModules } from '@/lib/use-my-modules';
import { useCompanies } from '@/lib/use-permissions';
import { buildFieldIssueMessages } from '@/lib/validation-toast';

// ─── Types (TODO T12.D.8: share with RecordFormModal) ───────────────────────

type ConditionalOperator = 'equals' | 'not_equals' | 'not_empty' | 'empty';

interface LayoutElement {
  id: string;
  /**
   * Typ elementu w layoucie. `'field'` → render przez `FieldRegistry`
   * (istniejący flow). Inny typ → render przez `LayoutElementRegistry` (T12.H.7).
   */
  type: string;
  /** Wypełnione TYLKO dla `type === 'field'`. */
  fieldId?: string;
  width: 1 | 2 | 3;
  settings: {
    label?: string | null;
    required?: boolean | null;
    conditionalField?: string | null;
    conditionalValue?: string | null;
    conditionalOperator?: ConditionalOperator | null;
    /** Per-typ konfiguracja layout elementu. */
    [key: string]: unknown;
  };
  style?: LayoutElementStyle;
}

interface LayoutSection {
  id: string;
  title: string | null;
  columns: 1 | 2 | 3;
  elements: LayoutElement[];
  /** Zakładka, do której należy sekcja (Krok 8). Brak/nieznane = pierwsza zakładka. */
  tabId?: string | null;
}

/** Zakładka układu formularza (Krok 8) — rejestr w layout_config.tabs. */
interface FormLayoutTab {
  id: string;
  slug: string;
  title: string;
}

interface CustomModuleLayout {
  id: string;
  /** Nazwa nadana w builderze (np. "Domyślny", "Uproszczony") — etykieta w C7 pickerze. */
  name: string;
  layoutType: 'form' | 'list';
  isDefault: boolean;
  layoutConfig: { sections: LayoutSection[]; tabs?: FormLayoutTab[] };
  /** T12.H.11 — przypisanie do kategorii. NULL = uniwersalny. */
  categoryId?: string | null;
  /** T12.H.11 — przypisanie do ról. Puste = dla wszystkich. */
  roleIds?: string[];
}

interface ModuleDetail {
  id: string;
  slug: string;
  name: string;
  fields: ModuleField[];
  layouts: CustomModuleLayout[];
}

interface ModuleRecord {
  id: string;
  record_name: string | null;
  category_id: string | null;
  record_number: string | null;
  is_archived: boolean;
  gcrecord: string | null;
  // T12.D.cm — back-link do rekordu źródła (gdy utworzony przez cross-module action)
  source_record_id: string | null;
  source_module_id: string | null;
  created_at: string;
  updated_at: string;
  [columnSlug: string]: unknown;
}

interface Category {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  showAsTab: boolean;
  /** F12.I: konfiguracja numeracji (V2 segment-based). Backend GET zwraca już V2
   * po normalize on read (E1.T5). NULL = numeracja wyłączona lub nie skonfigurowana. */
  numberingConfig: NumberingConfigV2 | null;
}

// T12.D.7 — Akcje per rekord
interface ModuleAction {
  id: string;
  name: string;
  icon: string;
  variant: 'primary' | 'secondary' | 'danger' | 'warning';
  position: 'record_actions' | 'toolbar';
  actionType: string;
  actionConfig: Record<string, unknown>;
  showConditions: Array<{ field: string; operator: string; value: unknown }>;
  requireConfirmation: boolean;
  confirmationMessage: string | null;
}

// ─── Helpers (TODO T12.D.8: share with RecordFormModal) ─────────────────────

function isElementVisible(element: LayoutElement, data: Record<string, unknown>): boolean {
  const { conditionalField, conditionalValue, conditionalOperator } = element.settings;
  if (!conditionalField || !conditionalOperator) return true;

  const rawValue = data[conditionalField];
  const strValue = rawValue != null ? String(rawValue) : '';
  const isEmpty =
    rawValue == null || strValue === '' || (Array.isArray(rawValue) && rawValue.length === 0);

  switch (conditionalOperator) {
    case 'empty':
      return isEmpty;
    case 'not_empty':
      return !isEmpty;
    case 'equals':
      return strValue === (conditionalValue ?? '');
    case 'not_equals':
      return strValue !== (conditionalValue ?? '');
    default:
      return true;
  }
}

/**
 * Kontekst procesów akceptacji rekordu dla klocków procesowych układu formularza
 * (`process_view`, `process_action_button`). Registry klocków (`tryRenderLayoutElement`) nie zna rekordu
 * ani modułu, więc strona przekazuje go do `ViewForm` / `EditForm` jednym obiektem —
 * mirror `contextRenderers['process_view']` na kartotekach systemowych.
 */
interface RecordProcessContext {
  /** Kod procesu modułu własnego: `custom.<slug>`. */
  moduleCode: string;
  /** Id rekordu; `null` w trybie tworzenia (rekord nie istnieje — brak historii). */
  recordId: string | null;
  /** Czy tenant ma aktywny moduł Procesy. */
  processesActive: boolean;
  /**
   * Kategoria rekordu (ta sama, którą dostaje baner) — „Przycisk akceptacji” podaje ją do
   * „Wyślij do akceptacji” (wybór procesu po kategorii); `null` = rekord bez kategorii.
   */
  categoryId: string | null;
  /**
   * Bramka zapisu przed oknem decyzji (ta sama co w banerze) — dla „Pokaż przyciski decyzji” i
   * „Przycisku akceptacji”.
   */
  beforeDecision?: DecisionGate;
  /** Czy formularz jest w trybie edycji (wpływa na to, czy element pokazuje panel decyzji). */
  editing: boolean;
}

/**
 * Wynik renderu klocka procesowego: `handled: false` = typ nieobsługiwany (wołający idzie
 * do `tryRenderLayoutElement`); `handled: true` z `node: null` = klocek procesowy nie ma
 * czego pokazać (tryb tworzenia / moduł Procesy nieaktywny) — znika z formularza, zamiast
 * pokazywać zaślepkę z kreatora.
 */
type ProcessElementRender = { handled: true; node: ReactNode } | { handled: false };

/** Render klocków procesowych z kontekstem rekordu (patrz `ProcessElementRender`). */
function renderProcessLayoutElement(
  element: LayoutElement,
  ctx: RecordProcessContext,
): ProcessElementRender {
  switch (element.type) {
    case 'process_view': {
      if (!ctx.recordId || !ctx.processesActive) return { handled: true, node: null };
      const cfg = parseProcessViewElementConfig(element.settings);
      return {
        handled: true,
        node: (
          <ProcessHistoryTab
            moduleCode={ctx.moduleCode}
            recordId={ctx.recordId}
            showCancelled={cfg.showCancelled}
            // Ograniczenie modułów własnych: w EDYCJI element nie pokazuje panelu decyzji (zostaje
            // tylko „Anuluj proces”). Widok i edycja to dwa osobne komponenty (`ViewForm` /
            // `EditForm`), więc po zapisie przed decyzją wyjście z edycji odmontowuje element wraz z
            // panelem i prośba o otwarcie okna decyzji by przepadła. Do zdjęcia, gdy formularz rekordu
            // modułu własnego stanie się jednym formularzem dla widoku i edycji.
            actions={
              cfg.showDecisionActions
                ? { beforeDecision: ctx.beforeDecision, decisions: !ctx.editing }
                : null
            }
          />
        ),
      };
    }
    case 'process_action_button': {
      // Ograniczenie modułów własnych (jak `decisions` przy `process_view` wyżej): w EDYCJI element
      // nie renderuje nic. Widok i edycja to dwa osobne komponenty (`ViewForm` / `EditForm`), więc po
      // zapisie przed decyzją wyjście z edycji odmontowuje element, a prośba o otwarcie okna
      // (trzymana w stanie przycisku) by przepadła. Do zdjęcia razem z `decisions`, gdy formularz
      // rekordu modułu własnego stanie się jednym formularzem dla widoku i edycji.
      if (!ctx.recordId || !ctx.processesActive || ctx.editing)
        return { handled: true, node: null };
      return {
        handled: true,
        node: (
          <ProcessActionButton
            moduleCode={ctx.moduleCode}
            recordId={ctx.recordId}
            categoryId={ctx.categoryId}
            beforeDecision={ctx.beforeDecision}
          />
        ),
      };
    }
    default:
      return { handled: false };
  }
}

/**
 * Render klocka układu innego niż pole (sekcja/nagłówek/alert/proces…) — wspólny dla
 * `ViewForm` i `EditForm`. Klocki procesowe najpierw (potrzebują kontekstu rekordu),
 * reszta → registry klocków. `formData` = dane rekordu (widok) lub bieżący stan edycji.
 */
function renderNonFieldLayoutElement(
  element: LayoutElement,
  formData: Record<string, unknown>,
  processContext: RecordProcessContext,
): ReactNode {
  const processResult = renderProcessLayoutElement(element, processContext);
  if (processResult.handled) return processResult.node;
  return tryRenderLayoutElement({
    element: {
      id: element.id,
      type: element.type,
      settings: element.settings as Record<string, unknown>,
      style: element.style as Record<string, unknown> | undefined,
      conditionalField: element.settings.conditionalField ?? null,
      conditionalOperator: element.settings.conditionalOperator ?? null,
      conditionalValue: element.settings.conditionalValue ?? null,
    },
    children: [],
    mode: 'form',
    formData,
  });
}

/**
 * Rozwiązuje kod błędu z `FieldRegistry.validate` (required/invalid_email/…) na polski
 * komunikat — namespace modułu. Reguły z kreatora (min/max/długość/wzorzec/daty) idą
 * OSOBNO przez wspólny `resolveRuleErrorMessage` (klucze `common.validationIssue.*`,
 * te same co dokumenty/kartoteki).
 */
function resolveFieldErrorMessage(code: string, t: (key: string) => string): string {
  const key = `tenant.modules.records.errors.${code}`;
  const translated = t(key);
  return translated === key ? code : translated;
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function RecordFormPage() {
  const { slug: moduleSlug, recordId: recordIdParam } = useParams<{
    slug: string;
    recordId?: string;
  }>();
  // T12.H.12 — UNIFIED: `/records/new` → recordIdParam === 'new'; `/records/new` z route
  // bez :recordId też zostawia undefined. Oba traktujemy jako create.
  const isCreate = !recordIdParam || recordIdParam === 'new';
  const recordId = isCreate ? null : recordIdParam!;

  const navigate = useNavigate();
  // Powrót na listę modułu niesie jej kontekst (`?mi=`) — patrz `useWithMenuItem`.
  const withMenuItem = useWithMenuItem();
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // T12.D.cm — prefill cross-module flow (tylko create)
  const prefill = useMemo<Record<string, unknown> | null>(() => {
    if (!isCreate) return null;
    const raw = searchParams.get('prefill');
    if (!raw) return null;
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [searchParams, isCreate]);
  const sourceRecordId = isCreate ? searchParams.get('source') : null;
  const sourceModuleId = isCreate ? searchParams.get('sourceModule') : null;
  const queryCategoryId = isCreate ? searchParams.get('category') : null;

  // W create zawsze jesteśmy w trybie „edycji" formularza — view mode tylko dla detail
  const [isEditing, setIsEditing] = useState(isCreate);
  const [editData, setEditData] = useState<Record<string, unknown>>({});
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  // C7 — ręczny wybór layoutu formularza z pickera (session-only, resetowany przy
  // zmianie kategorii). Ignorowany gdy pozycja menu wymusza layout (forcedFormLayoutId).
  const [manualLayoutOverrideId, setManualLayoutOverrideId] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    onConfirm: () => void;
  } | null>(null);

  // Firma-właściciel rekordu (mirror ContractorDetail.tsx/EmployeeDetail.tsx) — WYŁĄCZNIE
  // pole tworzenia, write-once. W trybie jednej aktywnej firmy interceptor (`lib/api.ts`)
  // dokleja `X-Company-Id` sam, ale w trybie "Wszystkie firmy" NIE ma skąd wziąć firmy —
  // user musi wybrać jawnie (CompanyPicker niżej), submit zablokowany w handleSave dopóki
  // `companyId` jest puste.
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const activeCompanyScope = useActiveCompanyScope();
  const companyPrefilledRef = useRef(false);

  // T12.D.5 — przefiltrowane opcje zależne
  const filterField = useDependentOptions(editData);
  const [activeTab, setActiveTab] = useState<'history' | 'attachments'>('history');

  /** T12.D.6 — Cascade defaults: po wyborze rekordu w polu relation → wypełnij inne pola. */
  async function applyCascade(field: ModuleField, value: unknown) {
    if (
      field.fieldType !== 'relation' ||
      !field.cascadeFromMap ||
      Object.keys(field.cascadeFromMap).length === 0 ||
      !field.relationModuleId ||
      value == null ||
      value === ''
    )
      return;

    const recordId = Array.isArray(value) ? (value as unknown[])[0] : value;
    if (!recordId) return;

    try {
      const res = await api.get<{ records: Record<string, unknown>[] }>(
        `/modules/${field.relationModuleId}/records?ids=${String(recordId)}`,
      );
      const targetRecord = res.data.records?.[0];
      if (!targetRecord) return;

      setEditData((prev) => {
        const next = { ...prev };
        for (const [targetSlug, localSlug] of Object.entries(field.cascadeFromMap!)) {
          if (targetSlug && localSlug && targetRecord[targetSlug] !== undefined) {
            next[localSlug] = targetRecord[targetSlug];
          }
        }
        return next;
      });
    } catch {
      // Cascade nieudany — user może wpisać ręcznie
    }
  }

  // ── Queries ─────────────────────────────────────────────────────────────────

  const moduleQuery = useQuery({
    queryKey: ['module-by-slug', moduleSlug],
    queryFn: async () => {
      const res = await api.get<{ module: ModuleDetail }>(`/modules/${moduleSlug}`);
      return res.data.module;
    },
    enabled: !!moduleSlug,
  });

  const recordQuery = useQuery({
    queryKey: ['module-record', moduleSlug, recordId],
    queryFn: async () => {
      const res = await api.get<{ record: ModuleRecord }>(
        `/modules/${moduleQuery.data!.id}/records/${recordId!}`,
      );
      return res.data;
    },
    // CREATE: nie ma rekordu do fetcha
    enabled: !isCreate && !!moduleQuery.data?.id && !!recordId,
  });

  const categoriesQuery = useQuery({
    queryKey: ['module-categories', moduleQuery.data?.id],
    queryFn: async () => {
      const res = await api.get<{ categories: Category[] }>(
        `/modules/${moduleQuery.data!.id}/categories`,
      );
      return res.data.categories;
    },
    enabled: !!moduleQuery.data?.id,
  });

  // Lista firm tenanta dla CompanyPicker (mirror ContractorDetail.tsx/EmployeeDetail.tsx) —
  // `GET /companies`, nie `me.companies` (zawężone do firm ZALOGOWANEGO usera) — super-user
  // bez UCA musi móc wybrać dowolną firmę tenanta przy tworzeniu.
  const companiesQ = useCompanies();
  const companyOptions: CompanyPickerOption[] = (companiesQ.data?.companies ?? []).map((c) => ({
    id: c.id,
    legalName: c.legalName,
  }));

  // T12.D.7 — Akcje per rekord (tylko w detail mode — nowy rekord nie ma akcji)
  const actionsQuery = useQuery({
    queryKey: ['module-actions', moduleQuery.data?.id],
    queryFn: async () => {
      const res = await api.get<{ actions: ModuleAction[] }>(
        `/modules/${moduleQuery.data!.id}/actions`,
      );
      return res.data.actions;
    },
    enabled: !isCreate && !!moduleQuery.data?.id,
  });

  // ── Derived ──────────────────────────────────────────────────────────────────

  // T12.E.10 — konfiguracja strony detalu (z localStorage per moduł)
  const pageConfig = usePageConfig(moduleQuery.data?.id ?? '');

  // T17.1.9 — moduł Procesy (F17): baner + sekcja „Akceptacje" na karcie rekordu.
  const { data: myModules } = useMyModules();
  const processesActive = myModules?.subscriptions.some((s) => s.code === 'processes') ?? false;

  const module = moduleQuery.data;
  const record = recordQuery.data?.record;

  // Tytuł rekordu — wspólny dla nagłówka strony i karty; rekord bez nazwy → początek id.
  const recordTitle = record ? record.record_name || `#${String(record.id).slice(0, 8)}` : null;

  // Karta rekordu: tytuł rekordu (nowy — nazwa kartoteki), a nie etykieta ze ścieżki.
  useTabLabel(
    !module
      ? null
      : isCreate
        ? `${module.name} · ${t('tenant.modules.records.createTitle')}`
        : recordTitle,
  );

  // F17 follow-up — wskaźnik „proces się uruchamia…" po create z automatyzacją
  // start_approval (uzbrajany hintem `processAutoStarting` z odpowiedzi create,
  // patrz createMutation.onSuccess niżej). Wołany bezwarunkowo (przed early returny) —
  // create→detail nie remontuje tę stronę (brak `key` w Route), więc hook musi żyć
  // przez całą transformację isCreate→detail.
  const autostart = useRecordProcessAutostart(
    module ? `custom.${module.slug}` : '',
    recordId ?? '',
  );

  const activeFields = useMemo(() => {
    if (!module) return [];
    return module.fields.filter((f) => !('gcrecord' in f && (f as { gcrecord?: string }).gcrecord));
  }, [module]);

  const fieldsById = useMemo(() => {
    const map = new Map<string, ModuleField>();
    for (const f of activeFields) map.set(f.id, f);
    return map;
  }, [activeFields]);

  const fieldsBySlug = useMemo(() => {
    const map = new Map<string, ModuleField>();
    for (const f of activeFields) map.set(f.slug, f);
    return map;
  }, [activeFields]);

  // Pozycja menu tenanta, przez którą klient wszedł (`?mi=<id>`). Gdy ma przypisany
  // layout formularza (`viewRef` = 'entitySlug:layoutId') dla TEJ encji — wymuszamy
  // ten layout ignorując kategorię (blokada wyboru layoutu z pozycji menu).
  const activeMenuItem = useActiveMenuItem();
  const forcedFormLayoutId = useMemo(() => {
    if (!activeMenuItem?.viewRef) return null;
    const parsed = parseMenuViewRef(activeMenuItem.viewRef);
    if (!parsed || parsed.entitySlug !== moduleSlug) return null;
    return parsed.viewSlug;
  }, [activeMenuItem?.viewRef, moduleSlug]);

  // C7 — layouty formularza dostępne do ręcznego wyboru w Kreatorze (picker nad
  // formularzem). Renderowany tylko gdy >1 po filtrze i brak wymuszenia z menu.
  const formLayoutOptions = useMemo(
    () => (module ? module.layouts.filter((l) => l.layoutType === 'form') : []),
    [module],
  );

  // Kategoria bieżącego rekordu (lub editData.categoryId gdy user edytuje i zmienia
  // kategorię) — używana zarówno do rozwiązania layoutu, jak i do resetu ręcznego
  // overridu przy jej zmianie (C7).
  const currentCategoryId = isEditing ? selectedTypeId : (record?.category_id ?? null);

  // T12.H.11 — hybrid layout per kategoria, fallback uniwersalny. Override z pozycji
  // menu (forcedFormLayoutId) WYGRYWA nad ręcznym wyborem z pickera (C7) — gdy
  // ustawione, ręczny wybór jest ignorowany (dropdown w ogóle nie jest renderowany).
  const activeFormLayout = useMemo(() => {
    if (!module) return null;
    return resolveActiveLayout(
      module.layouts,
      currentCategoryId,
      forcedFormLayoutId ?? manualLayoutOverrideId ?? undefined,
    );
  }, [module, currentCategoryId, forcedFormLayoutId, manualLayoutOverrideId]);

  // C7 — zmiana kategorii unieważnia ręczny wybór layoutu (inny zestaw layoutów per
  // kategoria może nie zawierać wcześniej wybranego id — kaskada rozwiąże na nowo).
  useEffect(() => {
    setManualLayoutOverrideId(null);
  }, [currentCategoryId]);

  const formSections = useMemo(
    () => activeFormLayout?.layoutConfig.sections ?? [],
    [activeFormLayout],
  );

  // Krok 8 — zakładki formularza z layout_config.tabs (brak klucza = płaski układ).
  const formTabs = useMemo(() => activeFormLayout?.layoutConfig.tabs ?? [], [activeFormLayout]);
  const [activeFormTabId, setActiveFormTabId] = useState<string | null>(null);
  useEffect(() => {
    setActiveFormTabId((prev) =>
      prev && formTabs.some((tab) => tab.id === prev) ? prev : (formTabs[0]?.id ?? null),
    );
  }, [formTabs]);

  /** Sekcje aktywnej zakładki (sekcje bez/z nieznanym tabId → pierwsza zakładka). */
  const visibleFormSections = useMemo(() => {
    if (formTabs.length === 0) return formSections;
    const known = new Set(formTabs.map((tab) => tab.id));
    const firstId = formTabs[0]!.id;
    const active = activeFormTabId && known.has(activeFormTabId) ? activeFormTabId : firstId;
    return formSections.filter(
      (s) => (s.tabId && known.has(s.tabId) ? s.tabId : firstId) === active,
    );
  }, [formSections, formTabs, activeFormTabId]);

  // CREATE init — po module + categories załadowane, ustaw initial data
  // z FieldRegistry.defaultValue + prefill cross-module + default category
  useEffect(() => {
    if (!isCreate || !module || activeFields.length === 0) return;
    const initial: Record<string, unknown> = {};
    for (const field of activeFields) {
      if (prefill && Object.prototype.hasOwnProperty.call(prefill, field.slug)) {
        initial[field.slug] = prefill[field.slug];
      } else {
        initial[field.slug] = FieldRegistry.defaultValue(field);
      }
    }
    setEditData(initial);
    const cats = categoriesQuery.data;
    const defType =
      queryCategoryId ?? cats?.find((rt) => rt.isDefault)?.id ?? cats?.[0]?.id ?? null;
    setSelectedTypeId(defType);
    setEditErrors({});
    // Mirror ContractorDetail.tsx/EmployeeDetail.tsx: reset firmy przy (re)wejściu w tryb
    // create — musi iść PRZED efektem prefill niżej (kolejność efektów w komponencie =
    // kolejność flushowania w tym samym commicie), inaczej nadpisałby świeży prefill.
    companyPrefilledRef.current = false;
    setCompanyId(null);
    setCompanyError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, module?.id, activeFields.length, categoriesQuery.data?.length]);

  // Core invariant (bez tworzenia rekordu bez jawnie wybranej konkretnej firmy) — mirror
  // ContractorDetail.tsx/EmployeeDetail.tsx. W trybie jednej aktywnej firmy prefill
  // `companyId` z aktywnego kontekstu; w trybie "Wszystkie firmy" pole zostaje puste i user
  // MUSI wybrać (CompanyPicker niżej), submit zablokowany w handleSave.
  useEffect(() => {
    if (!isCreate) return;
    if (companyPrefilledRef.current) return;
    if (activeCompanyScope.mode !== 'company' || !activeCompanyScope.companyId) return;
    companyPrefilledRef.current = true;
    const resolvedId = activeCompanyScope.companyId;
    setCompanyId((prev) => prev ?? resolvedId);
  }, [isCreate, activeCompanyScope.mode, activeCompanyScope.companyId]);

  // ── Edit helpers ─────────────────────────────────────────────────────────────

  function startEdit() {
    if (!record) return;
    const initial: Record<string, unknown> = {};
    for (const f of activeFields) {
      initial[f.slug] = record[f.slug] ?? null;
    }
    setEditData(initial);
    setEditErrors({});
    setSelectedTypeId(record.category_id ?? null);
    setIsEditing(true);
  }

  function cancelEdit() {
    setIsEditing(false);
    setEditErrors({});
  }

  function validateEdit(): boolean {
    // Pomiń layout elements (sekcje/headery/alerty) — one nie mają walidacji ani slug.
    const hiddenSlugs = new Set<string>();
    for (const section of formSections) {
      for (const element of section.elements) {
        if (element.type !== 'field' || !element.fieldId) continue;
        if (!isElementVisible(element, editData)) {
          const f = fieldsById.get(element.fieldId);
          if (f) hiddenSlugs.add(f.slug);
        }
      }
    }
    const newErrors: Record<string, string> = {};
    for (const field of activeFields) {
      if (hiddenSlugs.has(field.slug)) continue;
      // 1) required + format per typ (kody własne modułu). 2) reguły z kreatora
      // (min/max/długość/wzorzec/daty) — komunikat identyczny z systemowymi.
      const code = FieldRegistry.validate(editData[field.slug], field);
      if (code) {
        newErrors[field.slug] = resolveFieldErrorMessage(code, t);
        continue;
      }
      const ruleErr = FieldRegistry.validateRules(editData[field.slug], field);
      if (ruleErr) {
        newErrors[field.slug] = resolveRuleErrorMessage(ruleErr, t);
      }
    }
    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  /**
   * Serwerowy VALIDATION_ERROR → mapa `slug → komunikat` pod polem. Identycznie jak
   * dokumenty: `buildFieldIssueMessages` (polski z kodów Zoda, wygrywa) + fallback na
   * surowy komunikat serwera z `details` (reguły dat mają tam gotowe polskie zdanie).
   * Zwraca pustą mapę, gdy to nie był błąd walidacji pól.
   */
  function mapServerValidationToFields(err: unknown): Record<string, string> {
    const out: Record<string, string> = {};
    const details = extractFieldErrors(err);
    for (const [slug, messages] of Object.entries(details)) {
      if (messages[0]) out[slug] = messages[0];
    }
    Object.assign(out, buildFieldIssueMessages(err, t));
    return out;
  }

  // ── Mutations ────────────────────────────────────────────────────────────────

  // Klik decyzji w banerze procesu, gdy formularz jest w edycji: najpierw ten sam zapis co
  // „Zapisz", okno decyzji dopiero po jego sukcesie (useSaveBeforeDecision; „zapis już trwa"
  // sprawdza sam hak po kluczu `saveMutationKey` mutacji zapisu tej kopii formularza).
  // `succeeded` / `aborted` są wołane z `updateMutation.onSuccess/onError` niżej. Ten formularz
  // nie ma ostrzeżeń procedury kontrolnej, więc nie ma tu `onCancel`.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing: isEditing,
    recordId,
    save: handleSave,
  });

  // CREATE — POST nowy rekord, redirect na świeży detail
  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (!module) throw new Error('No module');
      // CORE INVARIANT: w trybie „Wszystkie firmy" interceptor (`lib/api.ts`) NIE wysyła
      // X-Company-Id (brak aktywnej firmy) — bez jawnego nagłówka backend (`requireCompany`)
      // odrzuciłby POST kodem MISSING_HEADER (mirror ContractorDetail.tsx/EmployeeDetail.tsx —
      // ten sam CORE INVARIANT komentarz tam). Jawny nagłówek per-request wygrywa z
      // interceptorem i działa identycznie w trybie jednej aktywnej firmy (nadpisuje tą samą
      // wartością, którą interceptor wysłałby sam).
      const res = await api.post<{ record: { id: string }; processAutoStarting?: boolean }>(
        `/modules/${module.id}/records`,
        {
          data: payload,
          categoryId: selectedTypeId ?? undefined,
          sourceRecordId: sourceRecordId ?? undefined,
          sourceModuleId: sourceModuleId ?? undefined,
        },
        companyId ? { headers: { 'X-Company-Id': companyId } } : undefined,
      );
      return {
        record: res.data.record,
        processAutoStarting: res.data.processAutoStarting ?? false,
      };
    },
    onSuccess: ({ record: newRecord, processAutoStarting }) => {
      toast.show({ variant: 'success', title: t('tenant.modules.records.toasts.created') });
      void queryClient.invalidateQueries({ queryKey: ['module-records', moduleSlug] });
      // Karta „nowy" ma zamienić się w kartę zapisanego rekordu (replaceTab) —
      // reset do podglądu, bo create→detail nie remontuje strony.
      setIsEditing(false);
      navigate(withMenuItem(`/modules/${moduleSlug}/${newRecord.id}`), {
        state: {
          replaceTab: true,
          ...(processAutoStarting ? { processAutoStarting: true } : {}),
        },
      });
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        const fieldErrors = mapServerValidationToFields(err);
        if (Object.keys(fieldErrors).length > 0) {
          setEditErrors(fieldErrors);
          toast.show({
            variant: 'error',
            title: t('tenant.modules.records.toasts.validationError'),
          });
          return;
        }
      }
      toast.show({ variant: 'error', title: t('tenant.modules.records.toasts.createError') });
    },
  });

  const updateMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.put(`/modules/${module!.id}/records/${recordId!}`, {
        data,
        categoryId: selectedTypeId,
      });
      return res.data;
    },
    onSuccess: async () => {
      toast.show({ variant: 'success', title: t('tenant.modules.records.toasts.updated') });
      queryClient.invalidateQueries({ queryKey: ['module-records', moduleSlug] });
      // Zapis mógł zmienić kategorię rekordu (a od niej zależą prawa obiegu): flagi „Wyślij do
      // akceptacji" / „Anuluj proces" odświeżamy RAZEM z rekordem (bez czekania na nie). Moduł i
      // rekord są tu zawsze znane (bez nich zapis w ogóle nie wystartował — patrz `mutationFn`).
      if (module && recordId) {
        void invalidateRecordInstances(queryClient, `custom.${module.slug}`, recordId);
      }
      // Awaitowane TYLKO odświeżenie samego rekordu: baner procesu bierze z niego kategorię, a
      // okno wysyłki do akceptacji (otwierane przez `succeeded()` niżej) prosi o listę procesów
      // dla BIEŻĄCEJ kategorii — bez czekania mogłoby się otworzyć z listą kategorii sprzed zapisu.
      // Reszta odświeżeń zostaje bez czekania.
      await queryClient.invalidateQueries({ queryKey: ['module-record', moduleSlug, recordId] });
      // `setIsEditing(false)` i `succeeded()` razem, bez `await` między nimi (wymóg haka).
      setIsEditing(false);
      // Zapis wywołany kliknięciem decyzji w banerze → teraz otwórz okno decyzji (no-op, gdy
      // zapis poszedł zwykłym „Zapisz").
      saveBeforeDecision.succeeded();
    },
    onError: (err) => {
      // Zapis wywołany kliknięciem decyzji w banerze: każdy błąd kończy sprawę (okno decyzji
      // się nie otworzy; formularz nie ma ostrzeżeń kontroli, więc nic nie czeka na user'a).
      saveBeforeDecision.aborted();
      if (isAxiosError(err)) {
        const fieldErrors = mapServerValidationToFields(err);
        if (Object.keys(fieldErrors).length > 0) {
          setEditErrors(fieldErrors);
          toast.show({
            variant: 'error',
            title: t('tenant.modules.records.toasts.validationError'),
          });
          return;
        }
      }
      toast.show({ variant: 'error', title: t('tenant.modules.records.toasts.updateError') });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () => {
      const isCurrentlyArchived = !!record?.is_archived;
      await api.put(`/modules/${module!.id}/records/${recordId!}`, {
        data: editData,
        isArchived: !isCurrentlyArchived,
      });
      return !isCurrentlyArchived;
    },
    onSuccess: (archived) => {
      toast.show({
        variant: 'success',
        title: archived
          ? t('tenant.modules.records.detail.toasts.archived')
          : t('tenant.modules.records.detail.toasts.unarchived'),
      });
      queryClient.invalidateQueries({ queryKey: ['module-record', moduleSlug, recordId] });
      queryClient.invalidateQueries({ queryKey: ['module-records', moduleSlug] });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.modules.records.detail.toasts.archiveError'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/modules/${module!.id}/records/${recordId!}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.records.toasts.deleted') });
      queryClient.invalidateQueries({ queryKey: ['module-records', moduleSlug] });
      navigate(withMenuItem(`/modules/${moduleSlug}`));
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.records.toasts.deleteError') });
    },
  });

  // T12.D.7 — Execute-action
  const executeActionMutation = useMutation({
    mutationFn: async ({ actionId }: { actionId: string }) => {
      if (!module || !record) throw new Error('No module/record');
      const res = await api.post<{
        success: boolean;
        redirectUrl?: string;
        openInNewTab?: boolean;
        record?: Record<string, unknown>;
      }>(`/modules/${module.id}/records/${String(record.id)}/execute-action/${actionId}`);
      return res.data;
    },
    onSuccess: (data) => {
      if (data.redirectUrl) {
        if (data.openInNewTab) {
          window.open(data.redirectUrl, '_blank');
        } else {
          navigate(data.redirectUrl);
        }
      }
      toast.show({ variant: 'success', title: t('tenant.modules.actions.toasts.executed') });
      queryClient.invalidateQueries({
        queryKey: ['module-record', module?.id, String(record?.id)],
      });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.actions.toasts.executeError') });
    },
  });

  /** Ewaluuje showConditions akcji na podstawie bieżącego rekordu */
  function isActionVisible(action: ModuleAction): boolean {
    if (!action.showConditions || action.showConditions.length === 0) return true;
    if (!record) return false;
    return action.showConditions.every(({ field, operator, value: condVal }) => {
      const rawVal = record[field];
      const strVal = rawVal != null ? String(rawVal) : '';
      const isEmpty = rawVal == null || strVal === '';
      switch (operator) {
        case 'empty':
          return isEmpty;
        case 'not_empty':
          return !isEmpty;
        case 'equals':
          return strVal === String(condVal ?? '');
        case 'not_equals':
          return strVal !== String(condVal ?? '');
        default:
          return true;
      }
    });
  }

  function handleExecuteAction(action: ModuleAction) {
    if (action.requireConfirmation) {
      setPendingConfirm({
        title: action.name,
        message: action.confirmationMessage ?? t('tenant.modules.actionButtons.defaultConfirm'),
        confirmLabel: t('common.confirm') ?? 'OK',
        onConfirm: () => executeActionMutation.mutate({ actionId: action.id }),
      });
    } else {
      executeActionMutation.mutate({ actionId: action.id });
    }
  }

  // Zwraca, czy zapis wystartował (`false` = zatrzymała go walidacja) — z tego korzysta
  // `useSaveBeforeDecision` (klik decyzji w banerze w edycji = najpierw ten sam zapis).
  function handleSave(): boolean {
    // Core invariant — mirror ContractorDetail.tsx/EmployeeDetail.tsx: blokuje submit w
    // trybie create dopóki `companyId` nie jest ustawiony (prefillowany automatycznie w
    // trybie konkretnej firmy, wymaga jawnego pick w trybie "Wszystkie firmy").
    if (isCreate && !companyId) {
      setCompanyError(t('common.companyRequired'));
      toast.show({ variant: 'error', title: t('common.companyRequired') });
      return false;
    }
    setCompanyError(null);
    if (!validateEdit()) return false;
    if (isCreate) {
      createMutation.mutate(editData);
    } else {
      updateMutation.mutate(editData);
    }
    return true;
  }

  function handleCancel() {
    if (isCreate) {
      // Wróć na listę modułu
      navigate(withMenuItem(`/modules/${moduleSlug}`));
    } else {
      cancelEdit();
    }
  }

  function handleArchive() {
    setPendingConfirm({
      title: record?.is_archived
        ? t('tenant.modules.records.detail.unarchive')
        : t('tenant.modules.records.detail.archive'),
      message: t('tenant.modules.records.detail.confirmArchive'),
      confirmLabel: t('common.confirm') ?? 'OK',
      onConfirm: () => {
        const current: Record<string, unknown> = {};
        if (record) {
          for (const f of activeFields) current[f.slug] = record[f.slug] ?? null;
        }
        setEditData(current);
        archiveMutation.mutate();
      },
    });
  }

  function handleDelete() {
    setPendingConfirm({
      title: t('common.delete'),
      message: t('tenant.modules.records.detail.confirmDelete'),
      confirmLabel: t('common.delete'),
      onConfirm: () => deleteMutation.mutate(),
    });
  }

  // ── Loading / error states ───────────────────────────────────────────────────

  if (!moduleSlug) {
    return <main className="p-6 text-text-tertiary">Brak parametrów URL.</main>;
  }

  if (moduleQuery.isLoading || (!isCreate && recordQuery.isLoading)) {
    return (
      <main className="flex min-h-full items-center justify-center bg-bg-secondary p-6">
        <Spinner size="lg" />
      </main>
    );
  }

  if (moduleQuery.isError || !module) {
    return (
      <main className="p-6">
        <p className="text-sm text-red-600">{t('tenant.modules.records.loadError')}</p>
      </main>
    );
  }

  // Detail mode: rekord MUSI istnieć
  if (!isCreate && (recordQuery.isError || !record)) {
    return (
      <main className="p-6">
        <p className="text-sm text-red-600">{t('tenant.modules.records.detail.notFound')}</p>
        <button
          className="mt-2 text-sm text-accent-600 hover:underline"
          onClick={() => navigate(withMenuItem(`/modules/${moduleSlug}`))}
        >
          ← {t('tenant.modules.records.detail.backToList')}
        </button>
      </main>
    );
  }

  const isMutating =
    updateMutation.isPending ||
    archiveMutation.isPending ||
    deleteMutation.isPending ||
    createMutation.isPending;

  // Display name: detail = z rekordu, create = „Nowy rekord"
  const recordDisplayName = isCreate ? t('tenant.modules.records.createTitle') : recordTitle!;

  // Kontekst klocków procesowych układu (np. „Proces akceptacji") — ViewForm i EditForm.
  // Niezależny od sekcji „Akceptacje" niżej: ta zostaje zawsze (duplikat to wybór autora układu).
  const processContext: RecordProcessContext = {
    moduleCode: `custom.${module.slug}`,
    recordId,
    processesActive,
    categoryId: record?.category_id ?? null,
    beforeDecision: saveBeforeDecision.beforeDecision,
    editing: isEditing,
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <main className="min-h-full bg-bg-secondary" data-testid="record-detail-page">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="border-b border-border-primary bg-bg-primary px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Breadcrumb */}
          <div className="flex min-w-0 items-center gap-1.5 text-sm">
            <button
              className="flex items-center gap-1 text-text-secondary hover:text-text-primary"
              onClick={() => navigate(withMenuItem(`/modules/${moduleSlug}`))}
              data-testid="record-form-back"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="truncate max-w-[160px]">{module.name}</span>
            </button>
            <span className="text-text-tertiary">/</span>
            <span className="truncate font-medium text-text-primary max-w-[240px]">
              {recordDisplayName}
            </span>
            {!isCreate && record!.record_number && (
              <span className="ml-1 inline-flex items-center shrink-0 rounded-full bg-bg-tertiary px-2 py-0.5 font-mono text-xs font-medium text-text-secondary">
                {record!.record_number}
              </span>
            )}
            {!isCreate &&
              (() => {
                const types = categoriesQuery.data;
                const rt = types?.find((r) => r.id === record!.category_id);
                return rt ? (
                  <span
                    className="ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium shrink-0"
                    style={{ backgroundColor: `${rt.color}22`, color: rt.color }}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: rt.color }}
                    />
                    {rt.name}
                  </span>
                ) : null;
              })()}
            {!isCreate && record!.is_archived && (
              <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                <Archive className="h-3 w-3" />
                {t('tenant.modules.records.detail.archived')}
              </span>
            )}
            {/* T12.D.cm — Badge „utworzono z X" linkujący do rekordu źródła (tylko detail) */}
            {!isCreate && record!.source_module_id && record!.source_record_id && (
              <SourceRecordBadge
                sourceModuleId={record!.source_module_id}
                sourceRecordId={record!.source_record_id}
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {/* C7 — Picker layoutu formularza: widoczny tylko gdy >1 layout formularza
                PO filtrze i brak wymuszenia z pozycji menu (forcedFormLayoutId wygrywa —
                wtedy dropdown jest całkowicie ukryty, patrz komentarz przy activeFormLayout). */}
            {!forcedFormLayoutId && formLayoutOptions.length > 1 && (
              <div className="flex items-center gap-1.5">
                <label
                  htmlFor="record-form-layout-picker"
                  className="text-xs font-medium text-text-secondary"
                >
                  {t('tenant.modules.records.layoutPicker.label')}
                </label>
                <Dropdown
                  id="record-form-layout-picker"
                  data-testid="record-form-layout-picker"
                  className="rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm text-text-primary"
                  value={manualLayoutOverrideId ?? activeFormLayout?.id ?? ''}
                  onChange={(v) => setManualLayoutOverrideId(v || null)}
                  options={formLayoutOptions.map((layout) => ({
                    value: layout.id,
                    label: layout.name,
                  }))}
                />
              </div>
            )}
            {/* T12.D.7 — Przyciski akcji (record_actions, tylko detail view mode) */}
            {!isCreate &&
              !isEditing &&
              actionsQuery.data
                ?.filter((a) => a.position === 'record_actions' && isActionVisible(a))
                .map((action) => (
                  <Button
                    key={action.id}
                    variant={action.variant as 'primary' | 'secondary' | 'danger'}
                    size="sm"
                    onClick={() => handleExecuteAction(action)}
                    loading={
                      executeActionMutation.isPending &&
                      executeActionMutation.variables?.actionId === action.id
                    }
                    disabled={executeActionMutation.isPending}
                    data-testid={`record-action-btn-${action.id}`}
                  >
                    {action.name}
                  </Button>
                ))}

            {/* Tryb tworzenia ZAWSZE z przyciskami formularza: po „Utwórz" karta „nowy" zostaje
                na /records/new (powłoka kart podmienia ją z opóźnieniem), a `isEditing` jest już
                false — przyciski podglądu czytałyby wtedy nieistniejący rekord. */}
            {isEditing || isCreate ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isMutating}
                  data-testid="record-form-cancel"
                >
                  <X className="mr-1 h-4 w-4" />
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  loading={createMutation.isPending || updateMutation.isPending}
                  disabled={isMutating}
                  data-testid="record-form-save"
                >
                  <Save className="mr-1 h-4 w-4" />
                  {isCreate ? t('common.create') : t('common.save')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={startEdit}
                  disabled={isMutating}
                  data-testid="record-detail-edit"
                >
                  <Pencil className="mr-1 h-4 w-4" />
                  {t('tenant.modules.records.detail.edit')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleArchive}
                  loading={archiveMutation.isPending}
                  disabled={isMutating}
                  data-testid="record-detail-archive"
                >
                  <Archive className="mr-1 h-4 w-4" />
                  {record!.is_archived
                    ? t('tenant.modules.records.detail.unarchive')
                    : t('tenant.modules.records.detail.archive')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  loading={deleteMutation.isPending}
                  disabled={isMutating}
                  data-testid="record-detail-delete"
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  {t('common.delete')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div className="p-6">
        {/* T17.1.9 — baner akceptacji (moduł Procesy aktywny, tryb detail). */}
        {!isCreate && processesActive && recordId ? (
          <div className="mb-6 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <ProcessApprovalBanner
                moduleCode={`custom.${module.slug}`}
                recordId={recordId}
                categoryId={record?.category_id ?? null}
                beforeDecision={saveBeforeDecision.beforeDecision}
              />
            </div>
            {/* F17 follow-up — wskaźnik „proces się uruchamia…" (poll uzbrojony hintem
                z create; patrz useRecordProcessAutostart). Znika sam po pojawieniu się
                instancji albo po timeout — bez akcji usera. */}
            <ProcessAutostartIndicator starting={autostart.starting} />
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Form panel (2/3) — w trybie edit dorzucamy accent border na inputach
              (sygnał wizualny „to pole jest edytowalne"). View mode zostawiamy
              neutralny — ramki w kolorze border-primary jak w podglądzie. */}
          <div className="lg:col-span-2">
            <div
              className={
                'rounded-lg border border-border-primary bg-bg-primary p-6' +
                (isEditing
                  ? ' [&_input]:border-accent-300 [&_textarea]:border-accent-300 [&_select]:border-accent-300'
                  : '')
              }
            >
              {/* Krok 8 — zakładki formularza (widoczne w obu trybach: podgląd + edycja) */}
              {formTabs.length > 0 && (
                <div
                  className="mb-4 flex flex-wrap gap-1 border-b border-border-primary"
                  data-testid="record-form-tabs"
                >
                  {formTabs.map((tab) => {
                    const isActive =
                      tab.id ===
                      (activeFormTabId && formTabs.some((x) => x.id === activeFormTabId)
                        ? activeFormTabId
                        : formTabs[0]!.id);
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveFormTabId(tab.id)}
                        className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium transition ${
                          isActive
                            ? 'border-accent-500 text-accent-600'
                            : 'border-transparent text-text-secondary hover:text-text-primary'
                        }`}
                        data-testid={`record-form-tab-${tab.slug}`}
                      >
                        {tab.title}
                      </button>
                    );
                  })}
                </div>
              )}
              {isEditing ? (
                <>
                  {/* Firma-właściciel — WYŁĄCZNIE tworzenie (write-once, mirror
                      ContractorDetail.tsx/EmployeeDetail.tsx). W trybie "Wszystkie firmy"
                      user musi wybrać jawnie — bez tego backend odrzuci POST
                      (MISSING_HEADER), patrz core invariant w handleSave/createMutation. */}
                  {isCreate && (
                    <div className="mb-4" data-testid="record-form-company-picker">
                      <FormField
                        label={t('tenant.modules.records.companyLabel')}
                        required
                        error={companyError ?? undefined}
                      >
                        <CompanyPicker
                          value={companyId}
                          onChange={(v) => {
                            setCompanyId(v);
                            if (v) setCompanyError(null);
                          }}
                          companies={companyOptions}
                          required
                          testId="record-form-company"
                        />
                      </FormField>
                    </div>
                  )}
                  {/* Selektor kategorii — widoczny gdy moduł ma >1 kategorię */}
                  {(categoriesQuery.data?.length ?? 0) > 1 && (
                    <div
                      className="mb-4 flex flex-wrap gap-2"
                      data-testid="record-detail-type-picker"
                    >
                      {categoriesQuery.data!.map((rt) => (
                        <button
                          key={rt.id}
                          type="button"
                          onClick={() => setSelectedTypeId(rt.id)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition ${
                            selectedTypeId === rt.id
                              ? 'border-current'
                              : 'border-border-primary text-text-secondary hover:border-current'
                          }`}
                          style={
                            selectedTypeId === rt.id
                              ? {
                                  borderColor: rt.color,
                                  color: rt.color,
                                  backgroundColor: `${rt.color}18`,
                                }
                              : undefined
                          }
                        >
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: rt.color }}
                          />
                          {rt.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <EditForm
                    sections={visibleFormSections}
                    fieldsById={fieldsById}
                    data={editData}
                    errors={editErrors}
                    filterField={filterField}
                    numberingConfig={
                      (categoriesQuery.data ?? []).find((c) => c.id === selectedTypeId)
                        ?.numberingConfig ?? null
                    }
                    isCreate={isCreate}
                    processContext={processContext}
                    onChange={(slug, value) => {
                      setEditData((prev) => {
                        const next = { ...prev, [slug]: value };
                        // Auto-clear pól zależnych (T12.D.5)
                        for (const f of activeFields) {
                          if (f.dependsOnFieldSlug === slug && f.dependsOnOptionMap) {
                            const parentStr = Array.isArray(value)
                              ? String((value as unknown[])[0] ?? '')
                              : String(value ?? '');
                            const allowed = f.dependsOnOptionMap[parentStr];
                            if (allowed) {
                              next[f.slug] = clearIfUnavailable(next[f.slug], {
                                ...f,
                                options: f.options?.filter((o) => allowed.includes(o.value)) ?? [],
                              });
                            }
                          }
                        }
                        return next;
                      });
                      // Cascade defaults (T12.D.6)
                      const changedField = activeFields.find((f) => f.slug === slug);
                      if (changedField) void applyCascade(changedField, value);
                      if (editErrors[slug]) {
                        setEditErrors((prev) => {
                          const next = { ...prev };
                          delete next[slug];
                          return next;
                        });
                      }
                    }}
                    fields={activeFields}
                  />
                </>
              ) : (
                record && (
                  <ViewForm
                    sections={visibleFormSections}
                    fieldsById={fieldsById}
                    fieldsBySlug={fieldsBySlug}
                    record={record}
                    fields={activeFields}
                    processContext={processContext}
                  />
                )
              )}
            </div>
          </div>

          {/* Right panel (1/3) — tylko w detail mode (history/attachments dla istniejącego rekordu).
              W create rekord nie ma jeszcze ID — brak panelu. */}
          {!isCreate && pageConfig.rightPanel.show && (
            <div className="lg:col-span-1">
              <div className="rounded-lg border border-border-primary bg-bg-primary">
                {/* Tabs — tylko te włączone w konfiguracji */}
                {(pageConfig.rightPanel.showHistory || pageConfig.rightPanel.showAttachments) && (
                  <div className="flex border-b border-border-primary">
                    {pageConfig.rightPanel.showHistory && (
                      <button
                        className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors ${
                          activeTab === 'history'
                            ? 'border-b-2 border-accent-600 text-accent-600'
                            : 'text-text-secondary hover:text-text-primary'
                        }`}
                        onClick={() => setActiveTab('history')}
                        data-testid="record-tab-history"
                      >
                        <Clock className="h-4 w-4" />
                        {t('tenant.modules.records.detail.historyTab')}
                      </button>
                    )}
                    {pageConfig.rightPanel.showAttachments && (
                      <button
                        className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors ${
                          activeTab === 'attachments'
                            ? 'border-b-2 border-accent-600 text-accent-600'
                            : 'text-text-secondary hover:text-text-primary'
                        }`}
                        onClick={() => setActiveTab('attachments')}
                        data-testid="record-tab-attachments"
                      >
                        <Paperclip className="h-4 w-4" />
                        {t('tenant.modules.records.detail.attachmentsTab')}
                      </button>
                    )}
                  </div>
                )}

                {/* Tab content */}
                <div className="p-4">
                  {/* Jeśli aktywna zakładka jest wyłączona — przełącz na dostępną */}
                  {activeTab === 'history' && pageConfig.rightPanel.showHistory ? (
                    <RecordHistoryTimeline
                      moduleId={module.id}
                      recordId={recordId!}
                      fieldNames={new Map(activeFields.map((f) => [f.slug, f.name]))}
                    />
                  ) : activeTab === 'attachments' && pageConfig.rightPanel.showAttachments ? (
                    <RecordAttachments
                      moduleId={module.id}
                      moduleSlug={module.slug}
                      recordId={recordId!}
                    />
                  ) : pageConfig.rightPanel.showHistory ? (
                    <RecordHistoryTimeline
                      moduleId={module.id}
                      recordId={recordId!}
                      fieldNames={new Map(activeFields.map((f) => [f.slug, f.name]))}
                    />
                  ) : pageConfig.rightPanel.showAttachments ? (
                    <RecordAttachments
                      moduleId={module.id}
                      moduleSlug={module.slug}
                      recordId={recordId!}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* T17.1.9 — sekcja „Akceptacje" (historia decyzji) pod formularzem. */}
        {!isCreate && processesActive && recordId ? (
          <section
            className="mt-6 rounded-lg border border-border-primary bg-bg-primary p-4"
            data-testid="record-approvals-section"
          >
            <h3 className="mb-3 text-sm font-semibold text-text-primary">
              {t('tenant.processes.history.sectionTitle')}
            </h3>
            <ProcessHistoryTab moduleCode={`custom.${module.slug}`} recordId={recordId} />
          </section>
        ) : null}
      </div>

      {pendingConfirm && (
        <ConfirmDialog
          open={true}
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          onConfirm={() => {
            pendingConfirm.onConfirm();
            setPendingConfirm(null);
          }}
          onClose={() => setPendingConfirm(null)}
        />
      )}
    </main>
  );
}

// ─── View mode ───────────────────────────────────────────────────────────────

interface ViewFormProps {
  sections: LayoutSection[];
  fieldsById: Map<string, ModuleField>;
  fieldsBySlug: Map<string, ModuleField>;
  record: ModuleRecord;
  fields: ModuleField[];
  /** Kontekst klocków procesowych układu (`renderProcessLayoutElement`). */
  processContext: RecordProcessContext;
}

function ViewForm({ sections, fieldsById, record, fields, processContext }: ViewFormProps) {
  const recordData = record as Record<string, unknown>;

  // Gdy brak layoutu — fallback: render wszystkich pól
  if (sections.length === 0 || sections.every((s) => s.elements.length === 0)) {
    return (
      <div className="space-y-4">
        {fields.map((field) => (
          <ViewField key={field.id} field={field} value={recordData[field.slug]} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sections.map((section) => {
        const colsClass =
          section.columns === 1
            ? 'grid-cols-1'
            : section.columns === 3
              ? 'grid-cols-3'
              : 'grid-cols-2';
        return (
          <div key={section.id} data-testid={`view-section-${section.id}`}>
            {section.title && (
              <h3 className="mb-3 text-sm font-semibold text-text-primary">{section.title}</h3>
            )}
            <div className={`grid gap-4 ${colsClass}`}>
              {section.elements.map((element) => {
                if (!isElementVisible(element, recordData)) return null;

                // T12.H.2 — Layout element (sekcja/header/alert/itd.) — view mode.
                // Klocki procesowe najpierw (potrzebują kontekstu rekordu), reszta → registry.
                if (element.type !== 'field') {
                  const layoutNode = renderNonFieldLayoutElement(
                    element,
                    recordData,
                    processContext,
                  );
                  if (!layoutNode) return null;
                  return (
                    <div
                      key={element.id}
                      className="col-span-full"
                      style={styleToCSS(element.style)}
                    >
                      {layoutNode}
                    </div>
                  );
                }

                // Pole — istniejący flow przez ViewField
                const field = element.fieldId ? fieldsById.get(element.fieldId) : undefined;
                if (!field) return null;
                const widthClass =
                  element.width === 3
                    ? 'col-span-3'
                    : element.width === 2
                      ? 'col-span-2'
                      : 'col-span-1';
                const displayField: ModuleField = {
                  ...field,
                  ...(element.settings.label != null
                    ? { name: element.settings.label as string }
                    : {}),
                };
                const { container: viewContainerStyle, text: viewTextStyle } = styleToCSSSplit(
                  element.style,
                );
                return (
                  <div key={element.id} className={widthClass}>
                    <ViewField
                      field={displayField}
                      value={recordData[field.slug]}
                      containerStyle={viewContainerStyle}
                      textStyle={viewTextStyle}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface ViewFieldProps {
  field: ModuleField;
  value: unknown;
  /** Container styles (bg, border, padding, margin) — wrapper div. */
  containerStyle?: React.CSSProperties;
  /** Text styles (color, font, decoration) — inner value box. */
  textStyle?: React.CSSProperties;
}

/**
 * ViewField — render pojedynczego pola w trybie podglądu rekordu.
 *
 * Decyzja UX (2026-05-11): wygląd IDENTYCZNY jak w edit mode (label normalny +
 * ramka + bg). Różnica tylko w interakcji: w view divwartość, w edit input.
 * Pozwala uniknąć nagłej zmiany layoutu przy klik „Edytuj" — pola pozostają
 * w tych samych miejscach z tymi samymi rozmiarami.
 */
function ViewField({ field, value, containerStyle, textStyle }: ViewFieldProps) {
  const rendered = FieldRegistry.renderCell(field, value);
  const isEmpty = rendered === '' || rendered === null || rendered === undefined;
  const hasCustomColor = textStyle?.color !== undefined;

  return (
    <div style={containerStyle} data-testid={`view-field-${field.slug}`}>
      <FormField label={field.name} required={field.isRequired}>
        {/*
          Div udający input — te same klasy co input w FieldRegistry types
          (w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm),
          plus min-h-[38px] żeby empty wyglądały tak samo jak wypełnione.
          textStyle (color) nadpisuje text-text-primary przez inline style specificity.
        */}
        <div
          className={`w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm min-h-[38px] ${
            hasCustomColor ? '' : 'text-text-primary'
          }`}
          style={textStyle}
        >
          {isEmpty ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            <span>{rendered as React.ReactNode}</span>
          )}
        </div>
      </FormField>
    </div>
  );
}

// ─── Edit mode ───────────────────────────────────────────────────────────────

interface EditFormProps {
  sections: LayoutSection[];
  fieldsById: Map<string, ModuleField>;
  data: Record<string, unknown>;
  errors: Record<string, string>;
  onChange: (slug: string, value: unknown) => void;
  fields: ModuleField[];
  filterField: (field: ModuleField) => ModuleField;
  /** F12.I E3.T1: config numeracji aktualnej kategorii (do disabled/hint pola docelowego). */
  numberingConfig: NumberingConfigV2 | null;
  /** F12.I E3.T1: tryb CREATE vs EDIT (różny behavior dla lockAfterGeneration). */
  isCreate: boolean;
  /** Kontekst klocków procesowych układu (`renderProcessLayoutElement`). */
  processContext: RecordProcessContext;
}

// ─── Numbering field behavior (F12.I E3.T1) ─────────────────────────────────

/**
 * Wynik kompute dla pola docelowego numeracji:
 *  - `disabled` — czy input jest readonly (auto-generated, brak overrride)
 *  - `hint` — pomocniczy tekst pod inputem (preview / lock info)
 */
interface NumberingFieldState {
  disabled: boolean;
  hint?: string;
}

/**
 * Liczy stan pola docelowego numeracji per `fieldSlug` w trybie EDIT/CREATE.
 * Reguły (F12.I D5/D6):
 *  - cfg.enabled === false LUB targetFieldSlug !== fieldSlug → brak zmian (disabled=false)
 *  - CREATE + !allowManualOverride → readonly + hint "Auto-generowany przy zapisie. Preview: X"
 *  - CREATE + allowManualOverride → editable + hint "Auto-generowany jeśli zostawisz puste. Preview: X"
 *  - EDIT + lockAfterGeneration && wartość obecna → readonly + hint "Numer zablokowany po wygenerowaniu"
 *  - EDIT bez locka → editable
 */
function computeNumberingFieldState(
  fieldSlug: string,
  cfg: NumberingConfigV2 | null,
  isCreate: boolean,
  currentValue: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): NumberingFieldState {
  if (!cfg?.enabled || cfg.targetFieldSlug !== fieldSlug) {
    return { disabled: false };
  }

  const hasValue = currentValue != null && String(currentValue).trim() !== '';

  if (isCreate) {
    const preview = previewRecordNumber(cfg);
    if (!cfg.allowManualOverride) {
      return {
        disabled: true,
        hint: t('tenant.numbering.runtime.autoGenerated', { preview }),
      };
    }
    return {
      disabled: false,
      hint: t('tenant.numbering.runtime.autoIfEmpty', { preview }),
    };
  }

  // EDIT
  if (cfg.lockAfterGeneration && hasValue) {
    return { disabled: true, hint: t('tenant.numbering.runtime.locked') };
  }
  return { disabled: false };
}

function EditForm({
  sections,
  fieldsById,
  data,
  errors,
  onChange,
  fields,
  filterField,
  numberingConfig,
  isCreate,
  processContext,
}: EditFormProps) {
  const { t } = useTranslation();

  // Fallback: brak layoutu
  if (sections.length === 0 || sections.every((s) => s.elements.length === 0)) {
    return (
      <div className="space-y-3">
        {fields.map((field) => {
          const numbering = computeNumberingFieldState(
            field.slug,
            numberingConfig,
            isCreate,
            data[field.slug],
            t,
          );
          return (
            <EditFieldRow
              key={field.id}
              field={filterField(field)}
              value={data[field.slug]}
              error={errors[field.slug]}
              onChange={(v) => onChange(field.slug, v)}
              disabled={numbering.disabled}
              hint={numbering.hint}
              formData={data}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {sections.map((section) => {
        const colsClass =
          section.columns === 1
            ? 'grid-cols-1'
            : section.columns === 3
              ? 'grid-cols-3'
              : 'grid-cols-2';
        return (
          <div key={section.id} data-testid={`edit-section-${section.id}`}>
            {section.title && (
              <h3 className="mb-2 text-sm font-semibold text-text-primary">{section.title}</h3>
            )}
            <div className={`grid gap-3 ${colsClass}`}>
              {section.elements.map((element) => {
                if (!isElementVisible(element, data)) return null;

                // T12.H.2 — Layout element — edit mode (te same elementy co view,
                // bo struktura strony nie zmienia się przy edycji). Klocki procesowe
                // najpierw (potrzebują kontekstu rekordu), reszta → registry.
                if (element.type !== 'field') {
                  const layoutNode = renderNonFieldLayoutElement(element, data, processContext);
                  if (!layoutNode) return null;
                  return (
                    <div
                      key={element.id}
                      className="col-span-full"
                      style={styleToCSS(element.style)}
                    >
                      {layoutNode}
                    </div>
                  );
                }

                // Pole — istniejący flow przez EditFieldRow
                const field = element.fieldId ? fieldsById.get(element.fieldId) : undefined;
                if (!field) return null;
                const widthClass =
                  element.width === 3
                    ? 'col-span-3'
                    : element.width === 2
                      ? 'col-span-2'
                      : 'col-span-1';
                const overriddenField = filterField({
                  ...field,
                  ...(element.settings.label != null
                    ? { name: element.settings.label as string }
                    : {}),
                  ...(element.settings.required != null
                    ? { isRequired: element.settings.required as boolean }
                    : {}),
                });
                const elementCss = styleToCSS(element.style);
                const numbering = computeNumberingFieldState(
                  field.slug,
                  numberingConfig,
                  isCreate,
                  data[field.slug],
                  t,
                );
                return (
                  <div key={element.id} className={widthClass} style={elementCss}>
                    <EditFieldRow
                      field={overriddenField}
                      value={data[field.slug]}
                      error={errors[field.slug]}
                      onChange={(v) => onChange(field.slug, v)}
                      disabled={numbering.disabled}
                      hint={numbering.hint}
                      formData={data}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface EditFieldRowProps {
  field: ModuleField;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
  /** F12.I E3.T1: input readonly (pole docelowe numeracji auto-generowany). */
  disabled?: boolean;
  /** F12.I E3.T1: pomocniczy tekst pod inputem (np. preview numeru / lock info). */
  hint?: string;
  /**
   * Etap 10.3: bieżące wartości wszystkich pól formularza (klucz = slug), przekazywane
   * do `FieldRegistry.renderInput` — czyta je m.in. `SelectFieldType` dla pól ze
   * słownikiem parametryzowanym `record_field` (wartości pól-sąsiadów).
   */
  formData?: Record<string, unknown>;
}

function EditFieldRow({
  field,
  value,
  error,
  onChange,
  disabled,
  hint,
  formData,
}: EditFieldRowProps) {
  // `error` w stanie to już gotowy polski komunikat (rozwiązany przy walidacji/z serwera).
  return (
    <FormField label={field.name} required={field.isRequired} error={error} hint={hint}>
      {FieldRegistry.renderInput({ field, value, onChange, error, disabled, formData })}
    </FormField>
  );
}
